import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { forwardRef } from "react";

import { cn } from "@/lib/utils";

export type PhoneVerificationInputProps = {
  readonly id?: string;
  readonly name: string;
  readonly value?: string;
  readonly placeholder?: string;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onChange?: (value: string) => void;
  readonly onFinalChange?: (value: string) => void;
  readonly "aria-invalid"?: boolean;
  readonly "aria-describedby"?: string;
};

const PhoneVerificationInput = forwardRef<
  HTMLDivElement,
  PhoneVerificationInputProps
>((props, ref): React.ReactElement => {
  const {
    id,
    name,
    placeholder = "",
    className = "",
    disabled = false,
    onChange,
    onFinalChange,
    ...ariaProps
  } = props;

  const input1Ref = useRef<HTMLInputElement>(null);
  const input2Ref = useRef<HTMLInputElement>(null);
  const input3Ref = useRef<HTMLInputElement>(null);
  const input4Ref = useRef<HTMLInputElement>(null);
  const input5Ref = useRef<HTMLInputElement>(null);
  const input6Ref = useRef<HTMLInputElement>(null);

  const inputRefs = useMemo(
    () => [input1Ref, input2Ref, input3Ref, input4Ref, input5Ref, input6Ref],
    []
  );

  const [internalValue, setInternalValue] = useState("");

  const digits = useMemo(() => {
    const splitDigits = internalValue.split("").slice(0, 6);
    while (splitDigits.length < 6) {
      splitDigits.push("");
    }
    return splitDigits;
  }, [internalValue]);

  const handleInputChange = useCallback(
    (index: number, inputValue: string) => {
      const numericValue = inputValue.replace(/\D/g, "");

      if (numericValue.length <= 1) {
        const currentDigits = internalValue.split("").slice(0, 6);
        while (currentDigits.length < 6) {
          currentDigits.push("");
        }

        const newDigits = [...currentDigits];
        newDigits[index] = numericValue;
        const concatenatedValue = newDigits.join("");

        setInternalValue(concatenatedValue);

        onChange?.(concatenatedValue);

        if (numericValue && index < 5) {
          const nextInput = inputRefs[index + 1];
          nextInput?.current?.focus();
        }
      }
    },
    [internalValue, onChange, onFinalChange, inputRefs]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      const currentDigits = internalValue.split("").slice(0, 6);
      while (currentDigits.length < 6) {
        currentDigits.push("");
      }

      if (e.key === "Backspace" && !currentDigits[index] && index > 0) {
        const prevInput = inputRefs[index - 1];
        prevInput?.current?.focus();
      } else if (e.key === "ArrowLeft" && index > 0) {
        const prevInput = inputRefs[index - 1];
        prevInput?.current?.focus();
      } else if (e.key === "ArrowRight" && index < 5) {
        const nextInput = inputRefs[index + 1];
        nextInput?.current?.focus();
      }
    },
    [internalValue, inputRefs]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, 6);

      setInternalValue(pastedData);

      onChange?.(pastedData);

      if (pastedData.length === 6) {
        onFinalChange?.(pastedData);
      }

      const nextIndex = Math.min(pastedData.length, 5);
      setTimeout(() => {
        const nextInput = inputRefs[nextIndex];
        nextInput?.current?.focus();
      }, 0);
    },
    [onChange, onFinalChange, inputRefs]
  );

  useEffect(() => {
    const firstInput = inputRefs[0];
    firstInput?.current?.focus();
  }, [inputRefs]);

  return (
    <div ref={ref} className={cn("flex justify-center gap-3", className)}>
      {Array.from({ length: 6 }, (_, index) => (
        <input
          key={index}
          ref={inputRefs[index]}
          aria-describedby={ariaProps["aria-describedby"]}
          aria-invalid={ariaProps["aria-invalid"]}
          className={cn(
            "h-12 w-10 rounded border text-center font-mono text-[16px] tracking-wider",
            "focus:border-[#c7b59e] focus:shadow-none focus:outline-none focus:ring-0",
            "transition-all duration-200",
            disabled
              ? "cursor-not-allowed border-white bg-white opacity-50"
              : digits[index]
              ? "border-[#c7b59e] bg-white"
              : "border-white bg-white"
          )}
          disabled={disabled}
          id={id ? `${id}-${index}` : undefined}
          inputMode="numeric"
          autoComplete="one-time-code"
          name={`${name}-${index}`}
          placeholder={placeholder}
          value={digits[index]}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={index === 0 ? handlePaste : undefined}
          maxLength={1}
        />
      ))}
    </div>
  );
});

PhoneVerificationInput.displayName = "PhoneVerificationInput";

export default PhoneVerificationInput;
